/**
 * PDF Formatting Utilities
 * Handles proper formatting for PDF generation with jsPDF
 */

/**
 * Format price for PDF display
 * Fixes the issue where thousand separators appear as "/" instead of spaces
 * Uses regular spaces instead of non-breaking spaces which can cause issues in PDF
 */
export function formatPriceForPDF(price: number): string {
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);

  // Replace non-breaking spaces and other problematic characters with regular spaces
  // This fixes the issue where "5 000 €" appears as "5/000 €" in PDF
  return formatted
    .replace(/\u00A0/g, ' ')  // Non-breaking space
    .replace(/\u202F/g, ' ')  // Narrow no-break space
    .replace(/\s+/g, ' ')      // Normalize multiple spaces
    .trim();
}

/**
 * Format number for PDF display (without currency symbol)
 */
export function formatNumberForPDF(value: number, decimals: number = 2): string {
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);

  return formatted
    .replace(/\u00A0/g, ' ')
    .replace(/\u202F/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * PDF Color definitions
 */
export const PDF_COLORS = {
  primary: [45, 27, 105] as [number, number, number],
  secondary: [60, 60, 60] as [number, number, number],
  highlight: [200, 50, 50] as [number, number, number],
  backgroundGray: [245, 245, 245] as [number, number, number],
  lightGray: [220, 220, 220] as [number, number, number],
  darkGray: [80, 80, 80] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
  itemBackground: [250, 250, 250] as [number, number, number]
} as const;

/**
 * PDF Font sizes
 */
export const PDF_FONT_SIZES = {
  title: 20,
  subtitle: 14,
  heading: 11,
  normal: 9,
  itemName: 9,
  details: 7,
  small: 8,
  tiny: 6.5
} as const;
